import { useId, useState } from "react";
import classNames from "classnames";

import Icons from "assets";
import { ActionsMenu, MenuItem } from "ui/actions-menu";
import { useSidebar } from "./sidebar-context";

import { SidebarAccount, SidebarAccountAction } from "./sidebar-menu.types";

export type SidebarAccountBlockProps = {
  account: SidebarAccount;
  actions: SidebarAccountAction[];
};

const SidebarAccountBlock = ({ account, actions }: SidebarAccountBlockProps) => {
  const { isCollapsed } = useSidebar();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuId = useId();

  const handleMenuOpen = () => setIsMenuOpen(true);
  const handleMenuClose = () => setIsMenuOpen(false);

  return (
    <div className="mt-auto border-t border-gray-200 pb-3 pt-2">
      <ActionsMenu
        itemName={account.name}
        open={isMenuOpen}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        placement="right"
        menuId={`sidebar-account-menu-${menuId}`}
        trigger={
          <button
            type="button"
            className={classNames(
              "flex w-full items-center h-14 pl-3.5 py-2 rounded-md justify-start focus:outline-none focus-visible:ring-2 focus-visible:ring-aqua-600 focus-visible:ring-offset-2",
              {
                "pr-6": !isCollapsed,
              },
            )}>
            <span className="inline-flex size-9 flex-shrink-0 items-center justify-center rounded-full bg-navy-100 text-sm font-semibold text-navy-800">
              <Icons.UserAvatar className="size-5" />
            </span>
            <div
              className={classNames(
                "min-w-0 flex-1 pl-2 text-left transition-all duration-200 overflow-hidden flex items-center justify-between whitespace-nowrap",
                isCollapsed ? "max-w-0 opacity-0" : "max-w-full opacity-100",
              )}>
              <span className="block min-w-0 flex-1 truncate pr-2">
                <span
                  className="block truncate text-sm font-bold leading-5 text-gray-900"
                  title={account.name}>
                  {account.name}
                </span>
                <span
                  className="block truncate text-sm font-semibold leading-5 text-gray-500"
                  title={account.role}>
                  {account.role}
                </span>
              </span>
              <Icons.ChevronRight className="size-4 flex-shrink-0 text-gray-900" />
            </div>
          </button>
        }>
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            icon={action.icon}
            label={action.label}
            data-testid={action.testId}
            to={action.to}
            onClick={() => {
              action.onClick();
              setIsMenuOpen(false);
            }}
          />
        ))}
      </ActionsMenu>
    </div>
  );
};

export default SidebarAccountBlock;
